<script setup>
import { computed, onMounted, reactive, ref, watch } from "vue";
import WorkforceService from "@/services/WorkforceService";
import { useUserStore } from "@/stores/userStore";
import { useEntitlementStore } from "@/stores/entitlementStore";

const userStore = useUserStore();
const entitlementStore = useEntitlementStore();
const now = new Date();
const monthKey = ref(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
const activeTab = ref("month");
const displayMode = ref("month");
const selectedWeek = ref("");
const selectedEmployeeId = ref("");
const monthData = ref(null);
const history = ref(null);
const leaveRequests = ref([]);
const approvals = ref([]);
const employees = ref([]);
const notice = ref("");
const selfProfileMissing = ref(false);
const approvalEdits = reactive({});
const odsFile = ref(null);
const odsPreview = ref(null);
const odsConfirmed = ref(false);
const odsApplying = ref(false);
const odsApplyError = ref("");
const importEmployeeId = ref("");

const timeForm = reactive({
  mode: "duration",
  workDate: localDate(now),
  grossMinutes: 480,
  startedAt: `${localDate(now)}T08:00`,
  endedAt: `${localDate(now)}T16:30`,
  breakMinutes: 30,
  note: "",
});

const allocationForm = reactive({
  workDate: localDate(now),
  allocatedMinutes: 0,
  customerReference: "",
  projectReference: "",
  activity: "",
  orderNumber: "",
  billable: false,
  internalTimeEntryId: "",
  description: "",
});

const leaveForm = reactive({
  startDate: localDate(now),
  endDate: localDate(now),
  requestedLeaveDays: 1,
  note: "",
});

const permissions = {
  selfRead: "workforce.self.read",
  timeWrite: "workforce.time.write",
  leaveRequest: "workforce.leave.request",
  teamRead: "workforce.team.read",
  leaveApprove: "workforce.leave.approve",
  auditRead: "workforce.audit.read",
  import: "workforce.import",
};

const hasWorkforceTime = computed(() => entitlementStore.hasModule("workforce-time"));
const hasLeave = computed(() => entitlementStore.hasModule("leave"));
const canSelfRead = computed(() => hasPermission(permissions.selfRead));
const canReadMonth = computed(() => hasWorkforceTime.value && canSelfRead.value);
const canWriteTime = computed(() =>
  hasWorkforceTime.value
    && !selfProfileMissing.value
    && hasPermission(permissions.timeWrite)
);
const canRequestLeave = computed(() =>
  hasLeave.value
    && !selfProfileMissing.value
    && hasPermission(permissions.leaveRequest)
);
const canReadTeam = computed(() =>
  hasWorkforceTime.value && hasPermission(permissions.teamRead)
);
const canApproveLeave = computed(() =>
  hasLeave.value && hasPermission(permissions.leaveApprove)
);
const canImport = computed(() =>
  hasWorkforceTime.value && hasPermission(permissions.import)
);
const canReadHistory = computed(() =>
  hasWorkforceTime.value
    && (
      (!selfProfileMissing.value && canSelfRead.value)
      || (Boolean(selectedEmployeeId.value)
        && (canReadTeam.value || hasPermission(permissions.auditRead)))
    )
);

const tabs = computed(() => [
  ...(canReadMonth.value || canReadTeam.value
    ? [{ id: "month", label: "Monat", icon: "bi-calendar3" }]
    : []),
  ...(canWriteTime.value ? [{ id: "time", label: "Zeit", icon: "bi-stopwatch" }] : []),
  ...(hasLeave.value && canSelfRead.value && !selfProfileMissing.value
    ? [{ id: "leave", label: "Urlaub", icon: "bi-calendar-check" }]
    : []),
  ...(canApproveLeave.value ? [{ id: "approvals", label: "Freigaben", icon: "bi-inbox" }] : []),
  ...(canReadHistory.value ? [{ id: "history", label: "Historie", icon: "bi-clock-history" }] : []),
  ...(canImport.value ? [{ id: "import", label: "ODS-Import", icon: "bi-file-earmark-spreadsheet" }] : []),
]);

const evaluation = computed(() => monthData.value?.evaluation || null);
const selectedEmployee = computed(() => monthData.value?.employee || null);
const weekOptions = computed(() => {
  const values = new Map();
  (evaluation.value?.days || []).forEach((day) => {
    const monday = mondayOf(day.date);
    if (!values.has(monday)) {
      values.set(monday, `KW ${isoWeek(day.date)} | ${formatDate(monday)}`);
    }
  });
  return Array.from(values, ([value, label]) => ({ value, label }));
});
const visibleDays = computed(() => {
  const days = evaluation.value?.days || [];
  if (displayMode.value === "month") return days;
  const week = selectedWeek.value || weekOptions.value[0]?.value;
  return days.filter((day) => mondayOf(day.date) === week);
});

function hasPermission(permission) {
  return userStore.hasPermission(permission);
}

function localDate(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function localDateTimeWithOffset(value) {
  if (!value) return null;
  const parsed = new Date(value);
  const offsetMinutes = -parsed.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(2, "0");
  const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, "0");
  return `${value}:00${sign}${hours}:${minutes}`;
}

function formatDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(new Date(`${value}T12:00:00`));
}

function weekday(value) {
  return new Intl.DateTimeFormat("de-DE", { weekday: "short" }).format(
    new Date(`${value}T12:00:00`)
  );
}

function formatMinutes(value) {
  const total = Number(value || 0);
  const sign = total < 0 ? "-" : "";
  const absolute = Math.abs(total);
  return `${sign}${Math.floor(absolute / 60)}:${String(absolute % 60).padStart(2, "0")} h`;
}

function formatDays(value) {
  return new Intl.NumberFormat("de-DE", { maximumFractionDigits: 2 }).format(Number(value || 0));
}

function mondayOf(value) {
  const date = new Date(`${value}T12:00:00`);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day + 1);
  return localDate(date);
}

function isoWeek(value) {
  const date = new Date(`${value}T12:00:00`);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  const firstThursday = new Date(date.getFullYear(), 0, 4);
  return 1 + Math.round(
    ((date - firstThursday) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7
  );
}

function statusClass(status) {
  return {
    Draft: "text-bg-secondary",
    Submitted: "text-bg-warning",
    Approved: "text-bg-success",
    Rejected: "text-bg-danger",
    Cancelled: "text-bg-light border text-dark",
  }[status] || "text-bg-light border text-dark";
}

function differenceClass(value) {
  if (value === 0) return "text-success";
  return value < 0 ? "text-danger" : "text-warning-emphasis";
}

function showNotice(message) {
  notice.value = message;
  window.setTimeout(() => {
    if (notice.value === message) notice.value = "";
  }, 5000);
}

async function loadMonth() {
  if (!hasWorkforceTime.value || (!canSelfRead.value && !selectedEmployeeId.value)) return;
  const [year, month] = monthKey.value.split("-").map(Number);
  try {
    monthData.value = selectedEmployeeId.value
      ? await WorkforceService.getEmployeeMonth(selectedEmployeeId.value, year, month)
      : await WorkforceService.getMyMonth(year, month);
    if (!selectedEmployeeId.value) selfProfileMissing.value = false;
  } catch (error) {
    if (!selectedEmployeeId.value && error?.response?.status === 404) {
      selfProfileMissing.value = true;
      monthData.value = null;
      return;
    }
    throw error;
  }
  if (!selectedWeek.value || !weekOptions.value.some((item) => item.value === selectedWeek.value)) {
    selectedWeek.value = weekOptions.value[0]?.value || "";
  }
}

async function loadEmployees() {
  if (!canReadTeam.value) return;
  employees.value = await WorkforceService.getEmployees();
  if (!importEmployeeId.value) {
    importEmployeeId.value = employees.value[0]?.employee?.employeeId || "";
  }
}

async function loadLeaveRequests() {
  if (!hasLeave.value || !canSelfRead.value || selfProfileMissing.value) return;
  leaveRequests.value = await WorkforceService.getLeaveRequests();
}

async function loadApprovals() {
  if (!canApproveLeave.value) return;
  approvals.value = await WorkforceService.getApprovals();
  approvals.value.forEach((candidate) => {
    if (!approvalEdits[candidate.request.id]) {
      approvalEdits[candidate.request.id] = {
        reviewedCreditedTimeMinutes: candidate.suggestedCreditedTimeMinutes,
        calculationExplanation: "",
        decisionNote: "",
        rejectionReason: "",
      };
    }
  });
}

async function loadHistory() {
  if (!canReadHistory.value || (selfProfileMissing.value && !selectedEmployeeId.value)) return;
  history.value = selectedEmployeeId.value
    ? await WorkforceService.getEmployeeHistory(selectedEmployeeId.value)
    : await WorkforceService.getMyHistory();
}

async function selectTab(tab) {
  activeTab.value = tab;
  if (tab === "history") await loadHistory();
  if (tab === "approvals") await loadApprovals();
}

async function submitTime() {
  const payload = {
    entryId: crypto.randomUUID(),
    workDate: timeForm.workDate,
    startedAt: timeForm.mode === "clock" ? localDateTimeWithOffset(timeForm.startedAt) : null,
    endedAt: timeForm.mode === "clock" ? localDateTimeWithOffset(timeForm.endedAt) : null,
    grossMinutes: timeForm.mode === "duration" ? Number(timeForm.grossMinutes) : null,
    breakMinutes: Number(timeForm.breakMinutes),
    note: timeForm.note || null,
  };
  await WorkforceService.recordTime(payload);
  showNotice("Arbeitszeit gespeichert.");
  if (canReadMonth.value) await loadMonth();
}

async function submitAllocation() {
  const payload = {
    allocationId: crypto.randomUUID(),
    workDate: allocationForm.workDate,
    allocatedMinutes: Number(allocationForm.allocatedMinutes),
    customerReference: allocationForm.customerReference || null,
    projectReference: allocationForm.projectReference || null,
    activity: allocationForm.activity || null,
    orderNumber: allocationForm.orderNumber || null,
    billable: allocationForm.billable,
    internalTimeEntryId: allocationForm.internalTimeEntryId || null,
    description: allocationForm.description || null,
  };
  await WorkforceService.recordAllocation(payload);
  showNotice("Kundenallokation gespeichert.");
  if (canReadMonth.value) await loadMonth();
}

async function createLeave() {
  await WorkforceService.createLeaveRequest({
    requestId: crypto.randomUUID(),
    startDate: leaveForm.startDate,
    endDate: leaveForm.endDate,
    requestedLeaveDays: Number(leaveForm.requestedLeaveDays),
    note: leaveForm.note || null,
  });
  showNotice("Urlaubsantrag als Entwurf gespeichert.");
  await loadLeaveRequests();
}

async function submitLeave(requestId) {
  await WorkforceService.submitLeaveRequest(requestId);
  showNotice("Urlaubsantrag eingereicht.");
  await Promise.all([loadLeaveRequests(), loadMonth()]);
}

async function cancelLeave(requestId) {
  if (!window.confirm("Urlaubsantrag stornieren?")) return;
  await WorkforceService.cancelLeaveRequest(requestId);
  showNotice("Urlaubsantrag storniert.");
  await loadLeaveRequests();
}

async function approve(candidate) {
  const edit = approvalEdits[candidate.request.id];
  await WorkforceService.approve(candidate.request.id, {
    reviewedCreditedTimeMinutes: candidate.requiresManualTimeReview
      ? Number(edit.reviewedCreditedTimeMinutes)
      : null,
    calculationExplanation: candidate.requiresManualTimeReview
      ? edit.calculationExplanation
      : null,
    decisionNote: edit.decisionNote || null,
  });
  showNotice("Urlaubsantrag freigegeben.");
  await loadApprovals();
}

async function reject(candidate) {
  const reason = approvalEdits[candidate.request.id].rejectionReason.trim();
  if (!reason) return;
  await WorkforceService.reject(candidate.request.id, reason);
  showNotice("Urlaubsantrag abgelehnt.");
  await loadApprovals();
}

function onOdsSelected(event) {
  odsFile.value = event.target.files?.[0] || null;
  odsPreview.value = null;
  odsConfirmed.value = false;
  odsApplyError.value = "";
}

async function previewOds() {
  odsApplyError.value = "";
  odsPreview.value = await WorkforceService.previewOds(importEmployeeId.value, odsFile.value);
  odsConfirmed.value = false;
}

async function applyOds() {
  odsApplying.value = true;
  odsApplyError.value = "";
  try {
    const result = await WorkforceService.applyOds(
      importEmployeeId.value,
      odsFile.value,
      odsPreview.value.contentHash
    );
    showNotice(
      `${result.insertedInternalTimeEntries} interne Zeiten und ${result.insertedCustomerAllocations} Allokationen übernommen.`
    );
    odsConfirmed.value = false;
    await previewOds();
  } catch {
    odsConfirmed.value = false;
    odsApplyError.value =
      "Übernahme fehlgeschlagen. Die Transaktion wurde zurückgesetzt; es wurden keine ODS-Zeilen gespeichert.";
  } finally {
    odsApplying.value = false;
  }
}

function exportHistory() {
  if (!history.value) return;
  const rows = [["Bereich", "Datum", "Typ", "Wert", "Referenz", "Notiz"]];
  history.value.timeAccountEntries.forEach((entry) => rows.push([
    "Zeitkonto", entry.effectiveDate, entry.type, entry.minutes, entry.sourceReference, entry.reason || "",
  ]));
  history.value.leaveEntries.forEach((entry) => rows.push([
    "Urlaub", entry.effectiveDate, entry.type, entry.days, entry.sourceReference, entry.reason || "",
  ]));
  history.value.internalTimeEntries.forEach((entry) => rows.push([
    "Interne Zeit", entry.workDate, entry.source, entry.netMinutes, entry.sourceReference || "", entry.note || "",
  ]));
  const csv = rows.map((row) => row.map(csvCell).join(";")).join("\r\n");
  const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `workforce-historie-${history.value.employee.employeeId}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function csvCell(value) {
  return `"${String(value ?? "").replaceAll('"', '""')}"`;
}

watch(monthKey, loadMonth);
watch(selectedEmployeeId, async () => {
  history.value = null;
  await loadMonth();
  if (activeTab.value === "history") await loadHistory();
});
watch(displayMode, () => {
  if (displayMode.value === "week" && !selectedWeek.value) {
    selectedWeek.value = weekOptions.value[0]?.value || "";
  }
});

onMounted(async () => {
  if (!userStore.me) await userStore.loadMe();
  if (!entitlementStore.isLoaded) await entitlementStore.loadCapabilities();
  if (hasWorkforceTime.value) {
    await loadEmployees();
    if (!canSelfRead.value && employees.value.length) {
      selectedEmployeeId.value = employees.value[0].employee.employeeId;
    } else if (canSelfRead.value) {
      await loadMonth();
    }
  }
  await Promise.all([
    ...(selfProfileMissing.value ? [] : [loadLeaveRequests()]),
    loadApprovals(),
  ]);
  if (!tabs.value.some((tab) => tab.id === activeTab.value)) {
    activeTab.value = tabs.value[0]?.id || "";
  }
});
</script>

<template>
  <div class="workforce-page text-start" data-testid="workforce-page">
    <header class="workforce-header">
      <div>
        <h1>Arbeitszeit und Urlaub</h1>
        <p v-if="selectedEmployee" class="mb-0 text-muted">
          {{ selectedEmployee.displayName }}
        </p>
      </div>
      <div class="header-controls">
        <select
          v-if="canReadTeam"
          v-model="selectedEmployeeId"
          class="form-select form-select-sm employee-select"
          aria-label="Mitarbeiter"
        >
          <option v-if="canSelfRead" value="">Eigene Daten</option>
          <option
            v-for="item in employees"
            :key="item.employee.employeeId"
            :value="item.employee.employeeId"
          >
            {{ item.employee.displayName }}
          </option>
        </select>
        <input v-model="monthKey" class="form-control form-control-sm month-input" type="month" aria-label="Monat" />
      </div>
    </header>

    <div v-if="notice" class="alert alert-success py-2" role="status" data-testid="workforce-notice">
      {{ notice }}
    </div>

    <section
      v-if="selfProfileMissing && !selectedEmployeeId"
      class="alert alert-info workforce-setup-state"
      role="status"
      data-testid="workforce-profile-missing"
    >
      <h2 class="h5 alert-heading">Arbeitszeit und Urlaub noch nicht eingerichtet</h2>
      <p class="mb-0">
        Für Ihr Benutzerkonto ist noch kein Mitarbeiterprofil hinterlegt. Ein Benutzer mit
        Workforce-Personalverwaltung muss das Konto einem Mitarbeiterprofil zuordnen.
      </p>
    </section>

    <nav class="workforce-tabs" aria-label="Workforce-Bereiche">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        type="button"
        class="tab-button"
        :class="{ active: activeTab === tab.id }"
        :data-testid="`workforce-tab-${tab.id}`"
        @click="selectTab(tab.id)"
      >
        <i class="bi" :class="tab.icon" aria-hidden="true"></i>
        {{ tab.label }}
        <span v-if="tab.id === 'approvals' && approvals.length" class="badge text-bg-danger">
          {{ approvals.length }}
        </span>
      </button>
    </nav>

    <section v-if="activeTab === 'month' && evaluation" data-testid="workforce-month">
      <div class="metric-grid">
        <div class="metric"><span>Soll</span><strong>{{ formatMinutes(evaluation.contractScheduledMinutes) }}</strong></div>
        <div class="metric"><span>Ist intern</span><strong data-testid="actual-minutes">{{ formatMinutes(evaluation.actualInternalMinutes) }}</strong></div>
        <div class="metric"><span>Kunden</span><strong>{{ formatMinutes(evaluation.customerAllocatedMinutes) }}</strong></div>
        <div class="metric"><span>Zeitkonto</span><strong>{{ formatMinutes(evaluation.timeAccountBalanceMinutes) }}</strong></div>
        <div class="metric"><span>Resturlaub</span><strong data-testid="leave-balance">{{ formatDays(evaluation.leaveBalanceDays) }} Tage</strong></div>
      </div>

      <div v-if="evaluation.warnings.length" class="alert alert-warning py-2 mt-3 mb-0">
        <div v-for="warning in evaluation.warnings" :key="warning">{{ warning }}</div>
      </div>

      <div class="table-toolbar">
        <div class="btn-group btn-group-sm" role="group" aria-label="Zeitraum">
          <button type="button" class="btn" :class="displayMode === 'month' ? 'btn-dark' : 'btn-outline-dark'" @click="displayMode = 'month'">Monat</button>
          <button type="button" class="btn" :class="displayMode === 'week' ? 'btn-dark' : 'btn-outline-dark'" @click="displayMode = 'week'">Woche</button>
        </div>
        <select v-if="displayMode === 'week'" v-model="selectedWeek" class="form-select form-select-sm week-select" aria-label="Kalenderwoche">
          <option v-for="week in weekOptions" :key="week.value" :value="week.value">{{ week.label }}</option>
        </select>
      </div>

      <div class="table-responsive workforce-table-wrap">
        <table class="table table-sm align-middle workforce-table" data-testid="month-days">
          <thead>
            <tr><th>Tag</th><th>Soll</th><th>Intern</th><th>Kunde</th><th>Differenz</th><th>Hinweis</th></tr>
          </thead>
          <tbody>
            <tr v-for="day in visibleDays" :key="day.date" :class="{ 'holiday-row': day.holiday }">
              <td><strong>{{ weekday(day.date) }}</strong><span>{{ formatDate(day.date) }}</span></td>
              <td>{{ day.scheduledMinutes == null ? "Prüfung" : formatMinutes(day.scheduledMinutes) }}</td>
              <td>{{ formatMinutes(day.actualInternalMinutes) }}</td>
              <td>{{ formatMinutes(day.customerAllocatedMinutes) }}</td>
              <td :class="differenceClass(day.allocationDifferenceMinutes)">{{ formatMinutes(day.allocationDifferenceMinutes) }}</td>
              <td class="day-note">
                <strong v-if="day.holiday">{{ day.holiday.name }}</strong>
                <span v-if="day.holidaySuggestion">{{ day.holidaySuggestion.explanation }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section v-if="activeTab === 'time'" class="split-layout" data-testid="workforce-time">
      <form class="workforce-panel" @submit.prevent="submitTime">
        <div class="panel-title"><i class="bi bi-clock" aria-hidden="true"></i><h2>Interne Arbeitszeit</h2></div>
        <div class="form-row two"><label>Datum<input v-model="timeForm.workDate" type="date" class="form-control" required /></label>
          <label>Pause in Minuten<input v-model.number="timeForm.breakMinutes" type="number" min="0" class="form-control" required /></label></div>
        <div class="btn-group btn-group-sm mb-3" role="group" aria-label="Erfassungsart">
          <button type="button" class="btn" :class="timeForm.mode === 'duration' ? 'btn-dark' : 'btn-outline-dark'" @click="timeForm.mode = 'duration'">Dauer</button>
          <button type="button" class="btn" :class="timeForm.mode === 'clock' ? 'btn-dark' : 'btn-outline-dark'" @click="timeForm.mode = 'clock'">Von / bis</button>
        </div>
        <label v-if="timeForm.mode === 'duration'">Bruttominuten<input v-model.number="timeForm.grossMinutes" type="number" min="1" max="1440" class="form-control" required /></label>
        <div v-else class="form-row two"><label>Beginn<input v-model="timeForm.startedAt" type="datetime-local" class="form-control" required /></label>
          <label>Ende<input v-model="timeForm.endedAt" type="datetime-local" class="form-control" required /></label></div>
        <label>Notiz<textarea v-model="timeForm.note" class="form-control" rows="2"></textarea></label>
        <button class="btn btn-primary command-button" type="submit" data-testid="save-time"><i class="bi bi-save" aria-hidden="true"></i> Arbeitszeit speichern</button>
      </form>

      <form class="workforce-panel" @submit.prevent="submitAllocation">
        <div class="panel-title"><i class="bi bi-briefcase" aria-hidden="true"></i><h2>Kundenallokation</h2></div>
        <div class="form-row two"><label>Datum<input v-model="allocationForm.workDate" type="date" class="form-control" required /></label>
          <label>Minuten<input v-model.number="allocationForm.allocatedMinutes" type="number" min="1" max="1440" class="form-control" required /></label></div>
        <label>Interne Zeit<select v-model="allocationForm.internalTimeEntryId" class="form-select"><option value="">Ohne direkte Zuordnung</option><option v-for="entry in monthData?.internalTimeEntries || []" :key="entry.id" :value="entry.id">{{ formatDate(entry.workDate) }} | {{ formatMinutes(entry.netMinutes) }}</option></select></label>
        <div class="form-check form-switch mb-3"><input id="billable" v-model="allocationForm.billable" class="form-check-input" type="checkbox" /><label for="billable" class="form-check-label">Abrechenbar</label></div>
        <div class="form-row two"><label>Kunde<input v-model="allocationForm.customerReference" class="form-control" :required="allocationForm.billable" /></label>
          <label>Projekt<input v-model="allocationForm.projectReference" class="form-control" :required="allocationForm.billable" /></label></div>
        <div class="form-row two"><label>Aktivität<input v-model="allocationForm.activity" class="form-control" /></label>
          <label>Auftragsnummer<input v-model="allocationForm.orderNumber" class="form-control" :required="allocationForm.billable" /></label></div>
        <label>Beschreibung<textarea v-model="allocationForm.description" class="form-control" rows="2"></textarea></label>
        <button class="btn btn-primary command-button" type="submit"><i class="bi bi-save" aria-hidden="true"></i> Allokation speichern</button>
      </form>
    </section>

    <section v-if="activeTab === 'leave'" data-testid="workforce-leave">
      <div class="leave-balance-band"><span>Verfügbarer Saldo</span><strong>{{ formatDays(evaluation?.leaveBalanceDays) }} Tage</strong></div>
      <div class="split-layout">
        <form v-if="canRequestLeave" class="workforce-panel" @submit.prevent="createLeave">
          <div class="panel-title"><i class="bi bi-calendar-plus" aria-hidden="true"></i><h2>Urlaubsantrag</h2></div>
          <div class="form-row two"><label>Von<input v-model="leaveForm.startDate" type="date" class="form-control" required /></label>
            <label>Bis<input v-model="leaveForm.endDate" type="date" class="form-control" required /></label></div>
          <label>Urlaubstage<input v-model.number="leaveForm.requestedLeaveDays" type="number" min="0.25" step="0.25" class="form-control" required /></label>
          <label>Notiz<textarea v-model="leaveForm.note" class="form-control" rows="3"></textarea></label>
          <button class="btn btn-primary command-button" type="submit"><i class="bi bi-save" aria-hidden="true"></i> Entwurf speichern</button>
        </form>
        <div class="workforce-panel">
          <div class="panel-title"><i class="bi bi-list-check" aria-hidden="true"></i><h2>Anträge</h2></div>
          <div v-if="!leaveRequests.length" class="empty-state">Keine Anträge vorhanden.</div>
          <div v-for="request in leaveRequests" :key="request.id" class="request-row">
            <div><strong>{{ formatDate(request.startDate) }} - {{ formatDate(request.endDate) }}</strong><span>{{ formatDays(request.requestedLeaveDays) }} Tage</span></div>
            <span class="badge" :class="statusClass(request.status)">{{ request.status }}</span>
            <div v-if="request.status === 'Draft'" class="row-actions">
              <button class="btn btn-sm btn-primary" type="button" @click="submitLeave(request.id)"><i class="bi bi-send" aria-hidden="true"></i> Einreichen</button>
              <button class="btn btn-sm btn-outline-danger" type="button" title="Stornieren" @click="cancelLeave(request.id)"><i class="bi bi-x-lg" aria-hidden="true"></i></button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section v-if="activeTab === 'approvals'" data-testid="workforce-approvals">
      <div v-if="!approvals.length" class="empty-state large">Keine offenen Freigaben.</div>
      <article v-for="candidate in approvals" :key="candidate.request.id" class="approval-item">
        <div class="approval-head"><div><h2>{{ candidate.employee.displayName }}</h2><span>{{ formatDate(candidate.request.startDate) }} - {{ formatDate(candidate.request.endDate) }} | {{ formatDays(candidate.request.requestedLeaveDays) }} Tage</span></div>
          <span v-if="candidate.requiresManualTimeReview" class="badge text-bg-warning">Prüfung erforderlich</span></div>
        <p>{{ candidate.explanation }}</p>
        <div v-if="candidate.requiresManualTimeReview" class="form-row two">
          <label>Geprüfte Gutschrift in Minuten<input v-model.number="approvalEdits[candidate.request.id].reviewedCreditedTimeMinutes" type="number" min="0" class="form-control" required /></label>
          <label>Berechnungsgrundlage<input v-model="approvalEdits[candidate.request.id].calculationExplanation" class="form-control" required /></label>
        </div>
        <label>Entscheidungsnotiz<input v-model="approvalEdits[candidate.request.id].decisionNote" class="form-control" /></label>
        <div class="approval-actions"><button type="button" class="btn btn-success" :disabled="!candidate.canApprove" @click="approve(candidate)"><i class="bi bi-check-lg" aria-hidden="true"></i> Freigeben</button>
          <input v-model="approvalEdits[candidate.request.id].rejectionReason" class="form-control" placeholder="Ablehnungsgrund" />
          <button type="button" class="btn btn-outline-danger" :disabled="!approvalEdits[candidate.request.id].rejectionReason.trim()" @click="reject(candidate)"><i class="bi bi-x-lg" aria-hidden="true"></i> Ablehnen</button></div>
      </article>
    </section>

    <section v-if="activeTab === 'history'" data-testid="workforce-history">
      <div class="section-actions"><button type="button" class="btn btn-outline-secondary btn-sm" @click="exportHistory"><i class="bi bi-download" aria-hidden="true"></i> CSV</button></div>
      <div v-if="history" class="history-grid">
        <div class="workforce-panel"><div class="panel-title"><h2>Vertragsversionen</h2></div><div class="table-responsive"><table class="table table-sm"><thead><tr><th>Gültig</th><th>Wochenstunden</th><th>Muster</th><th>Kurzarbeit</th></tr></thead><tbody><tr v-for="contract in history.contractVersions" :key="contract.id"><td>{{ formatDate(contract.validFrom) }} - {{ formatDate(contract.validThrough) }}</td><td>{{ formatMinutes(contract.weeklyMinutes) }}</td><td>{{ contract.workPattern.kind }}</td><td>{{ contract.shortTimeReductionPercent }} %</td></tr></tbody></table></div></div>
        <div class="workforce-panel"><div class="panel-title"><h2>Zeitkonto</h2></div><div class="table-responsive"><table class="table table-sm"><thead><tr><th>Datum</th><th>Typ</th><th>Minuten</th><th>Grund</th></tr></thead><tbody><tr v-for="entry in history.timeAccountEntries" :key="entry.id"><td>{{ formatDate(entry.effectiveDate) }}</td><td>{{ entry.type }}</td><td>{{ entry.minutes }}</td><td>{{ entry.reason }}</td></tr></tbody></table></div></div>
        <div class="workforce-panel"><div class="panel-title"><h2>Urlaubsledger</h2></div><div class="table-responsive"><table class="table table-sm"><thead><tr><th>Datum</th><th>Typ</th><th>Tage</th><th>Grund</th></tr></thead><tbody><tr v-for="entry in history.leaveEntries" :key="entry.id"><td>{{ formatDate(entry.effectiveDate) }}</td><td>{{ entry.type }}</td><td>{{ formatDays(entry.days) }}</td><td>{{ entry.reason }}</td></tr></tbody></table></div></div>
        <div class="workforce-panel"><div class="panel-title"><h2>Antragshistorie</h2></div><div v-for="request in history.leaveRequests" :key="request.id" class="history-request"><strong>{{ formatDate(request.startDate) }} - {{ formatDate(request.endDate) }}</strong><span v-for="event in request.history" :key="event.id">{{ event.status }} | {{ new Date(event.occurredAtUtc).toLocaleString('de-DE') }}<template v-if="event.note"> | {{ event.note }}</template></span></div></div>
      </div>
    </section>

    <section v-if="activeTab === 'import'" data-testid="workforce-import">
      <div class="workforce-panel import-panel">
        <div class="panel-title"><i class="bi bi-file-earmark-spreadsheet" aria-hidden="true"></i><h2>ODS-Differenzprüfung</h2></div>
        <div class="form-row two"><label>Mitarbeiter<select v-model="importEmployeeId" class="form-select" required><option value="" disabled>Auswählen</option><option v-for="item in employees" :key="item.employee.employeeId" :value="item.employee.employeeId">{{ item.employee.displayName }}</option></select></label>
          <label>ODS-Datei<input type="file" accept=".ods,application/vnd.oasis.opendocument.spreadsheet" class="form-control" required @change="onOdsSelected" /></label></div>
        <button type="button" class="btn btn-primary command-button" :disabled="!odsFile || !importEmployeeId" @click="previewOds"><i class="bi bi-search" aria-hidden="true"></i> Vorschau erstellen</button>
      </div>
      <div v-if="odsApplyError" class="alert alert-danger mt-3 mb-0" role="alert" data-testid="ods-apply-error">
        {{ odsApplyError }}
      </div>
      <div v-if="odsPreview" class="workforce-panel mt-3">
        <div class="import-summary"><span class="text-success">{{ odsPreview.newItems }} neu</span><span>{{ odsPreview.identicalItems }} identisch</span><span class="text-danger">{{ odsPreview.conflictItems }} Konflikte</span><span class="text-danger">{{ odsPreview.invalidItems }} ungültig</span></div>
        <div class="table-responsive"><table class="table table-sm"><thead><tr><th>Zeile</th><th>Schlüssel</th><th>Bereich</th><th>Status</th><th>Details</th></tr></thead><tbody><tr v-for="difference in odsPreview.differences" :key="`${difference.sheetNumber}-${difference.rowNumber}-${difference.entityKind}`"><td>{{ difference.sheetName }} / {{ difference.rowNumber }}</td><td>{{ difference.importKey }}</td><td>{{ difference.entityKind }}</td><td>{{ difference.status }}</td><td>{{ difference.summary }}<span v-for="detail in difference.details" :key="detail">{{ detail }}</span></td></tr></tbody></table></div>
        <div class="apply-row"><div class="form-check"><input id="confirm-ods" v-model="odsConfirmed" class="form-check-input" type="checkbox" :disabled="!odsPreview.canApply || odsApplying" /><label class="form-check-label" for="confirm-ods">Geprüfte Vorschau verbindlich übernehmen</label></div><button type="button" class="btn btn-success" :disabled="!odsPreview.canApply || !odsConfirmed || odsApplying" @click="applyOds"><span v-if="odsApplying" class="spinner-border spinner-border-sm" aria-hidden="true"></span><i v-else class="bi bi-check-lg" aria-hidden="true"></i> Übernehmen</button></div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.workforce-page { max-width: 1480px; margin: 0 auto; padding: 24px 24px 48px; color: #202428; }
.workforce-header { display: flex; align-items: end; justify-content: space-between; gap: 20px; padding-bottom: 18px; border-bottom: 1px solid #d9dee3; }
.workforce-header h1 { margin: 0; font-size: 1.75rem; font-weight: 700; letter-spacing: 0; }
.header-controls { display: flex; gap: 10px; align-items: center; }
.employee-select { min-width: 220px; }
.month-input { width: 160px; }
.workforce-tabs { display: flex; gap: 4px; overflow-x: auto; padding: 14px 0 18px; border-bottom: 1px solid #d9dee3; margin-bottom: 22px; }
.tab-button { border: 0; background: transparent; color: #4f5963; padding: 8px 12px; font-weight: 600; white-space: nowrap; border-bottom: 3px solid transparent; }
.tab-button.active { color: #17212b; border-bottom-color: #0b7285; }
.tab-button .bi { margin-right: 6px; }
.tab-button .badge { margin-left: 6px; }
.metric-grid { display: grid; grid-template-columns: repeat(5, minmax(150px, 1fr)); border-block: 1px solid #d9dee3; }
.metric { min-height: 88px; padding: 16px 18px; border-right: 1px solid #d9dee3; display: flex; flex-direction: column; justify-content: center; }
.metric:last-child { border-right: 0; }
.metric span { color: #68727c; font-size: .82rem; text-transform: uppercase; }
.metric strong { margin-top: 4px; font-size: 1.35rem; }
.table-toolbar { min-height: 54px; display: flex; align-items: center; gap: 12px; justify-content: flex-end; }
.week-select { width: 210px; }
.workforce-table-wrap { border: 1px solid #d9dee3; }
.workforce-table { margin: 0; table-layout: fixed; }
.workforce-table th { background: #f3f5f6; color: #48515a; font-size: .78rem; text-transform: uppercase; }
.workforce-table td { height: 54px; }
.workforce-table td:first-child span { display: block; color: #6a747d; font-size: .8rem; }
.workforce-table .day-note { width: 34%; font-size: .82rem; }
.day-note strong, .day-note span { display: block; }
.holiday-row { background: #fff8df; }
.split-layout { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 32px; }
.workforce-panel { padding: 0 0 24px; border-bottom: 1px solid #d9dee3; }
.panel-title { min-height: 46px; display: flex; align-items: center; gap: 9px; border-bottom: 2px solid #30363c; margin-bottom: 18px; }
.panel-title h2 { margin: 0; font-size: 1.05rem; font-weight: 700; }
.form-row { display: grid; gap: 14px; }
.form-row.two { grid-template-columns: repeat(2, minmax(0, 1fr)); }
label { display: block; margin-bottom: 14px; color: #414a52; font-size: .9rem; font-weight: 600; }
label .form-control, label .form-select { margin-top: 5px; font-weight: 400; }
.command-button { margin-top: 4px; }
.command-button .bi, .section-actions .bi, .approval-actions .bi { margin-right: 5px; }
.leave-balance-band { display: flex; align-items: baseline; gap: 16px; padding: 14px 18px; margin-bottom: 24px; background: #eaf4f1; border-left: 4px solid #2b8a6e; }
.leave-balance-band strong { font-size: 1.4rem; }
.request-row { display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 14px; padding: 12px 0; border-bottom: 1px solid #e1e5e8; }
.request-row div:first-child span { display: block; color: #68727c; font-size: .85rem; }
.row-actions { display: flex; gap: 6px; }
.approval-item { border: 1px solid #cfd6dc; border-radius: 6px; padding: 18px; margin-bottom: 14px; background: #fff; }
.approval-head { display: flex; justify-content: space-between; gap: 16px; align-items: start; }
.approval-head h2 { font-size: 1.05rem; margin: 0 0 4px; }
.approval-item p { color: #56616b; margin: 14px 0; }
.approval-actions { display: grid; grid-template-columns: auto minmax(220px, 1fr) auto; gap: 10px; align-items: center; }
.empty-state { color: #68727c; padding: 18px 0; }
.empty-state.large { border-block: 1px solid #d9dee3; padding: 36px 0; text-align: center; }
.section-actions { display: flex; justify-content: flex-end; margin-bottom: 12px; }
.history-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 28px; }
.history-request { padding: 10px 0; border-bottom: 1px solid #e1e5e8; }
.history-request span { display: block; margin-top: 3px; color: #5b6570; font-size: .84rem; }
.import-panel { max-width: 900px; }
.import-summary { display: flex; gap: 24px; padding: 10px 0 16px; font-weight: 700; }
.apply-row { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding-top: 12px; }
@media (max-width: 900px) {
  .workforce-page { padding: 18px 14px 36px; }
  .workforce-header { align-items: stretch; flex-direction: column; }
  .header-controls { align-items: stretch; flex-direction: column; }
  .employee-select, .month-input { width: 100%; }
  .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .metric { border-bottom: 1px solid #d9dee3; }
  .split-layout, .history-grid { grid-template-columns: 1fr; }
  .approval-actions { grid-template-columns: 1fr; }
  .request-row { grid-template-columns: 1fr auto; }
  .row-actions { grid-column: 1 / -1; }
}
@media (max-width: 560px) {
  .metric-grid, .form-row.two { grid-template-columns: 1fr; }
  .metric { border-right: 0; }
  .workforce-table { min-width: 780px; }
  .approval-head, .apply-row { flex-direction: column; }
}
</style>
